import { computed, Injectable, Signal, signal } from '@angular/core';
import { IncidentCountsByMonthPerPeriodModel } from '@app/models/insights/incident-counts-by-month-per-period.model';
import { InsightsModel } from '@app/models/insights/insights.model';
import { CountsByDataModel } from './models/counts-by-data.model';
import { AllDataRow } from './models/all-data.model';
import { ChartAggregationType } from '@app/enums/chart-aggregation-type';
import { IncidentsByGeoLocation } from '@app/models/insights/incidents-by-geo-location.model';
import { MotivationDescriptor } from '@app/enums/motivation';
import { Country, CountryDescriptor } from '@app/enums/country';
import { BusinessSectorDescriptor } from '@app/enums/business-sector';
import { ContinentDescriptor } from '@app/enums/continent';
import { EntitySizeDescriptor } from '@app/enums/entity-size';

// This Store should only be provided as scoped in the InsightsPage
// All child components of the page will inherit the same instance
// Modals however do not inherit the injection scope of the page/child component
// Opening modals requires you to provide the angular injector of the current component to the dialog.open({ injector: this.injector })
@Injectable()
export class InsightsPageStore {
  private readonly DEFAULT_TOPN_ITEMS_TO_CONSOLIDATE: number = 5;

  // State
  private rawData = signal<InsightsModel | null>(null);

  // Public setters
  public setData(data: InsightsModel): void {
    this.rawData.set(data);
  }

  // Computed/Aggregated data

  public readonly earliestIncidentStartDate: Signal<string | null> = computed(() => {
    const data = this.rawData();
    return data ? data.earliestIncidentDate : null;
  });

  // 1. Incident Counts

  public readonly chartAggregationType: Signal<ChartAggregationType | null> = computed(() => {
    const data = this.rawData();
    return data ? data.aggregationType : null;
  });

  public readonly incidentCountsAggregationLabels: Signal<string[] | null> = computed(() => {
    const data = this.rawData();
    return data ? data.incidentCountsAggregationLabels : null;
  });

  public readonly incidentCountsForMultiplePeriods: Signal<IncidentCountsByMonthPerPeriodModel[] | null> = computed(
    () => {
      const data = this.rawData();
      return data ? data.incidentsCountsByMonthForMultiplePeriods : null;
    },
  );

  public readonly incidentCountsToAllData: Signal<AllDataRow[]> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return [];
    }

    return data.incidentsCountsByMonthForMultiplePeriods.flatMap(
      (period) =>
        period.counts
          .map(
            (count, i) =>
              ({
                label: period.aggregationValues[i],
                count,
              }) as AllDataRow,
          )
          .filter((row) => row.count) // we can have periods without a count (no data for that period). Fiter out from the all data result
          .reverse(), // we also revense the periods data because we want a single period aggregations to be shown in a descending order
    );
  });

  public readonly selectedPeriodIncidentsCount: Signal<number | null> = computed(() => {
    const data = this.rawData();
    // The selected period is the topmost one in the collection. The others are comparison periods from previous years.
    return data ? data.incidentsCountsByMonthForMultiplePeriods[0].counts.reduce((acc, curr) => acc + curr, 0) : null;
  });

  // 2. By Motivation

  public readonly incidentsByMotivation: Signal<CountsByDataModel | null> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return null;
    }

    return {
      labels: data.incidentsByMotivation.groupedKeys.map((motivation) => MotivationDescriptor[motivation]),
      counts: data.incidentsByMotivation.counts,
      percentages: data.incidentsByMotivation.percentages,
    };
  });

  public readonly incidentsToMotivationToAllData: Signal<AllDataRow[]> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return [];
    }

    return data.incidentsByMotivation.groupedKeys.map(
      (motivationEnum, i) =>
        ({
          label: MotivationDescriptor[motivationEnum],
          count: data.incidentsByMotivation.counts[i],
          percentage: data.incidentsByMotivation.percentages[i],
        }) as AllDataRow,
    );
  });

  public readonly incidentsByMotivationConsolidated: Signal<CountsByDataModel | null> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return null;
    }

    return this.consolidateTopItems({
      items: data.incidentsByMotivation.groupedKeys.map((motivation) => MotivationDescriptor[motivation]),
      counts: data.incidentsByMotivation.counts,
      percentages: data.incidentsByMotivation.percentages,
    });
  });

  // 3. By Geo Location

  public readonly geoLocations: Signal<IncidentsByGeoLocation[] | null> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return null;
    }

    return data.geoLocations;
  });

  public readonly geoLocationsCountriesToAllData: Signal<AllDataRow[]> = computed(() => {
    const geoLocationsCountries = this.getSortedGeoLocationsCountriesFlat();
    if (!geoLocationsCountries) {
      return [];
    }

    return geoLocationsCountries.map(
      (geoLocationsCountry) =>
        ({
          label: CountryDescriptor[geoLocationsCountry.country],
          count: geoLocationsCountry.count,
          percentage: geoLocationsCountry.percentage,
        }) as AllDataRow,
    );
  });

  public readonly geoLocationsCountriesConsolidated: Signal<CountsByDataModel | null> = computed(() => {
    const geoLocationsCountries = this.getSortedGeoLocationsCountriesFlat();
    if (!geoLocationsCountries) {
      return null;
    }

    return this.consolidateTopItems({
      items: geoLocationsCountries.map((geoLocationsCountry) => CountryDescriptor[geoLocationsCountry.country]),
      counts: geoLocationsCountries.map((geoLocationsCountry) => geoLocationsCountry.count),
      percentages: geoLocationsCountries.map((geoLocationsCountry) => geoLocationsCountry.percentage),
    });
  });

  private getSortedGeoLocationsCountriesFlat(): { country: Country; count: number; percentage: number }[] | null {
    const geoLocations = this.geoLocations();

    if (!geoLocations) {
      return null;
    }

    const countryGroupedKeys = geoLocations.flatMap((geoLocation) => geoLocation.countryGroupedKeys);
    const countryCounts = geoLocations.flatMap((geoLocation) => geoLocation.countryCounts);
    const countryPercentagesToGlobal = geoLocations.flatMap((geoLocation) => geoLocation.countryPercentagesToGlobal);

    return countryGroupedKeys
      .map((country, index) => ({
        country: country,
        count: countryCounts[index],
        percentage: countryPercentagesToGlobal[index],
      }))
      .sort((a, b) => b.percentage - a.percentage);
  }

  public readonly geoLocationsContinentsToAllData: Signal<AllDataRow[]> = computed(() => {
    const geoLocationsContinents = this.getSortedGeoLocationsContinentsFlat();
    if (!geoLocationsContinents) {
      return [];
    }

    return geoLocationsContinents.map(
      (geoLocationsContinent) =>
        ({
          label: geoLocationsContinent.continent,
          count: geoLocationsContinent.incidentCount,
          percentage: geoLocationsContinent.continentPercentage,
        }) as AllDataRow,
    );
  });

  public readonly geoLocationsContinentsConsolidated: Signal<CountsByDataModel | null> = computed(() => {
    const geoLocationsContinents = this.getSortedGeoLocationsContinentsFlat();
    if (!geoLocationsContinents) {
      return null;
    }

    return this.consolidateTopItems({
      items: geoLocationsContinents.map((geoLocationsContinent) => geoLocationsContinent.continent),
      counts: geoLocationsContinents.map((geoLocationsContinent) => geoLocationsContinent.incidentCount),
      percentages: geoLocationsContinents.map((geoLocationsContinent) => geoLocationsContinent.continentPercentage),
    });
  });

  private getSortedGeoLocationsContinentsFlat():
    { continent: string; incidentCount: number; continentPercentage: number }[] | null {
    const geoLocations = this.geoLocations();

    if (!geoLocations) {
      return null;
    }

    return geoLocations
      .map((geoLocation) => ({
        continent: ContinentDescriptor[geoLocation.continent],
        incidentCount: geoLocation.countryCounts.reduce(
          (previousValue: number, currentValue: number) => previousValue + currentValue,
          0,
        ),
        continentPercentage: geoLocation.continentPercentageToGlobal,
      }))
      .sort((a, b) => b.continentPercentage - a.continentPercentage);
  }

  // 4. By Threat Actor

  public readonly incidentsByThreatActorToAllData: Signal<AllDataRow[]> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return [];
    }

    return data.incidentsByThreatActor.groupedKeys.map(
      (actorName, i) =>
        ({
          label: actorName,
          count: data.incidentsByThreatActor.counts[i],
          percentage: data.incidentsByThreatActor.percentages[i],
        }) as AllDataRow,
    );
  });

  public readonly incidentsByThreatActorConsolidated: Signal<CountsByDataModel | null> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return null;
    }

    return this.consolidateTopItems({
      items: data.incidentsByThreatActor.groupedKeys.map((actorName) => actorName),
      counts: data.incidentsByThreatActor.counts,
      percentages: data.incidentsByThreatActor.percentages,
    });
  });

  // 5. Affected Entity

  // Entity type

  public readonly incidentsByBusinessSectorToAllData: Signal<AllDataRow[]> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return [];
    }

    return data.affectedEntity.incidentsByBusinessSector.groupedKeys.map(
      (businessSectorEnum, i) =>
        ({
          label: BusinessSectorDescriptor[businessSectorEnum],
          count: data.affectedEntity.incidentsByBusinessSector.counts[i],
          percentage: data.affectedEntity.incidentsByBusinessSector.percentages[i],
        }) as AllDataRow,
    );
  });

  public readonly incidentsByBusinessSectorConsolidated: Signal<CountsByDataModel | null> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return null;
    }

    return this.consolidateTopItems(
      {
        items: data.affectedEntity.incidentsByBusinessSector.groupedKeys.map(
          (businessSectorEnum) => BusinessSectorDescriptor[businessSectorEnum],
        ),
        counts: data.affectedEntity.incidentsByBusinessSector.counts,
        percentages: data.affectedEntity.incidentsByBusinessSector.percentages,
      },
      this.DEFAULT_TOPN_ITEMS_TO_CONSOLIDATE,
      'Remaining sectors',
    ); // The consolidated section is called "Remaining sectors" here because the BusinessSector enum already has a "Other" type and the data clashes.
  });

  // Entity size

  public readonly incidentsByEntitySizeToAllData: Signal<AllDataRow[]> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return [];
    }

    return data.affectedEntity.incidentsByEntitySize.groupedKeys.map(
      (entitySizeEnum, i) =>
        ({
          label: EntitySizeDescriptor[entitySizeEnum],
          count: data.affectedEntity.incidentsByEntitySize.counts[i],
          percentage: data.affectedEntity.incidentsByEntitySize.percentages[i],
        }) as AllDataRow,
    );
  });

  public readonly incidentsByEntitySize: Signal<CountsByDataModel | null> = computed(() => {
    const data = this.rawData();

    if (!data) {
      return null;
    }

    // Passing a high number as the topN parameter in order to never consolidate the values in this particular case.
    // We always want to show all and not create "Other" section.
    return this.consolidateTopItems(
      {
        items: data.affectedEntity.incidentsByEntitySize.groupedKeys.map(
          (entitySizeEnum) => EntitySizeDescriptor[entitySizeEnum],
        ),
        counts: data.affectedEntity.incidentsByEntitySize.counts,
        percentages: data.affectedEntity.incidentsByEntitySize.percentages,
      },
      100,
    );
  });

  // RIMS

  public readonly rimsStepsByPhases_TechnicalInitialAccess_Consolidated: Signal<CountsByDataModel | null> = computed(
    () => this.rimsStepsByPhasesToConsolidated(0),
  );
  public readonly rimsStepsByPhases_PostExploitation_Consolidated: Signal<CountsByDataModel | null> = computed(() =>
    this.rimsStepsByPhasesToConsolidated(1),
  );
  public readonly rimsStepsByPhases_TechnicalImpact_Consolidated: Signal<CountsByDataModel | null> = computed(() =>
    this.rimsStepsByPhasesToConsolidated(2),
  );
  public readonly rimsStepsByPhases_NonTechnicalImpact_Consolidated: Signal<CountsByDataModel | null> = computed(() =>
    this.rimsStepsByPhasesToConsolidated(3),
  );

  private rimsStepsByPhasesToConsolidated(index: number): CountsByDataModel | null {
    const data = this.rawData();

    if (!data) {
      return null;
    }

    return this.consolidateTopItems({
      items: data.rimsStepsByPhase[index].stepNames,
      counts: data.rimsStepsByPhase[index].counts,
      percentages: data.rimsStepsByPhase[index].percentages,
    });
  }

  public readonly rimsStepsByPhases_TechnicalInitialAccess_ToAllData: Signal<AllDataRow[]> = computed(() =>
    this.rimsStepsByPhaseToAllDataRows(0),
  );
  public readonly rimsStepsByPhases_PostExploitation_ToAllData: Signal<AllDataRow[]> = computed(() =>
    this.rimsStepsByPhaseToAllDataRows(1),
  );
  public readonly rimsStepsByPhases_TechnicalImpact_ToAllData: Signal<AllDataRow[]> = computed(() =>
    this.rimsStepsByPhaseToAllDataRows(2),
  );
  public readonly rimsStepsByPhases_NonTechnicalImpact_ToAllData: Signal<AllDataRow[]> = computed(() =>
    this.rimsStepsByPhaseToAllDataRows(3),
  );

  private rimsStepsByPhaseToAllDataRows(index: number): AllDataRow[] {
    const data = this.rawData();
    if (!data) {
      return [];
    }

    return data.rimsStepsByPhase[index].stepNames.map(
      (stepName, i) =>
        ({
          label: stepName,
          count: data.rimsStepsByPhase[index].counts[i],
          percentage: data.rimsStepsByPhase[index].percentages[i],
        }) as AllDataRow,
    );
  }

  // Private members

  // Takes the raw incidents by motivation data and consolidates it so it contains the top 5 items with the highest count and the rest of
  // the categories are combined as one item named 'Other'. The counts and percentages are summed for the 'Other' item.
  // Example of consolidated data:
  // ItemName             | Count  | %     |
  // Attack / Destruction | 135    | 30.20 |
  // Hacktivism           | 94     | 21.48 |
  // Notoriety            | 50     | 11.19 |
  // Opportunistic        | 48     | 10.74 |
  // Financial Gain       | 40     |  8.95 |  <- up to the 5th item by default
  // Other                | 80     | 17.90 |  <- the rest of the items are combined here
  private consolidateTopItems(
    data: { items: string[]; counts: number[]; percentages: number[] },
    topN = this.DEFAULT_TOPN_ITEMS_TO_CONSOLIDATE,
    otherRowLabel = 'Other',
  ): CountsByDataModel {
    // If we have N or fewer items, return as-is
    if (data.items.length <= topN) {
      return {
        labels: data.items,
        counts: data.counts,
        percentages: data.percentages,
      };
    }

    // Take top N items
    const labels = data.items.slice(0, topN);
    const counts = data.counts.slice(0, topN);
    const percentages = data.percentages.slice(0, topN);

    // Sum up the remaining items for "Other"
    const otherCount = data.counts.slice(topN).reduce((sum, count) => sum + count, 0);
    const otherPercentage = data.percentages.slice(topN).reduce((sum, pct) => sum + pct, 0);

    // Add "Other" at the end (or whatever is being passed as a label)
    labels.push(otherRowLabel);
    counts.push(otherCount);
    percentages.push(otherPercentage);

    return { labels, counts, percentages };
  }
}
